import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';

type TranslationKeyConfig = {
  sourceKey: string;
  labelKey?: string;
  suffix?: string | (TranslationKeyConfig | string)[];
  transform?: 'number' | 'date' | 'translate';
  decimalPlaces?: number;
  dateFormat?: string;
  showFalsy?: boolean;
};

@Pipe({
  name: 'taskContentTranslations',
  standalone: true,
  pure: true,
})
export class TaskContentTranslationsPipe implements PipeTransform {
  constructor(
    private translateService: TranslateService,
    private datePipe: DatePipe,
  ) { }

  transform(
    entry: Record<string, unknown>,
    translationKeys: TranslationKeyConfig[] = [],
    _lang?: string,
  ): string[] {
    const content: string[] = [];

    for (const config of translationKeys) {
      const value = this.getNestedValue(entry, config.sourceKey);

      if (
        value !== undefined &&
        value !== null &&
        (config.showFalsy || value)
      ) {
        let displayValue = this.formatValue(value, config);

        displayValue += this.processSuffix(config.suffix, entry);

        // Add translated label prefix if provided
        if (config.labelKey) {
          const label = this.translateService.instant(config.labelKey);
          displayValue = `${label} : ${displayValue}`;
        }

        content.push(displayValue);
      }
    }

    return content;
  }

  private getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((current: unknown, key: string) => {
      if (current && typeof current === 'object') {
        return (current as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);
  }

  private processSuffix(
    suffix: string | (TranslationKeyConfig | string)[] | undefined,
    entry: Record<string, unknown>
  ): string {
    if (!suffix) {
      return '';
    }
    if (typeof suffix === 'string') {
      return suffix;
    }
    if (Array.isArray(suffix)) {
      let result = '';
      for (const suffixConfig of suffix) {
        if (typeof suffixConfig === 'string') {
          result += suffixConfig;
        } else {
          if (suffixConfig.transform === 'translate') {
            result += this.formatValue(
              suffixConfig.sourceKey,
              suffixConfig,
            );
            continue;
          }
          const suffixValue = this.getNestedValue(
            entry,
            suffixConfig.sourceKey,
          );
          if (suffixValue !== undefined && suffixValue !== null) {
            result += this.formatValue(suffixValue, suffixConfig);
          }
        }
      }
      return result;
    }
    return '';
  }

  private formatValue(value: unknown, config: TranslationKeyConfig): string {
    if (!config.transform) {
      return String(value);
    }
    if (config.transform === 'translate') {
      return this.translateService.instant(String(value));
    }

    if (config.transform === 'number' && typeof value === 'number') {
      const decimalPlaces = config.decimalPlaces ?? 2;
      return value.toFixed(decimalPlaces);
    }

    if (config.transform === 'date') {
      const dateFormat = config.dateFormat ?? 'dd/MM/yyyy';
      let formattedDate: string | null = null;
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        value instanceof Date
      ) {
        formattedDate = this.datePipe.transform(value, dateFormat);
      }
      return formattedDate ?? String(value);
    }

    return String(value);
  }
}
